import { useFormContext } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { AddMiniIcon, ArrowChevronDownMiniIcon, ArrowChevronRightMiniIcon, ArrowChevronUpMiniIcon, DollarSign } from '@calendly/ui/components/icons';
import { Menu, Item, BareItem } from '@calendly/ui/components/menu';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { BodyText } from '@calendly/ui/components/typography';
import { colorTextActionContrast, colorIconActiveContrast } from '@calendly/ui/theme';
import classNames from 'classnames';
import { useEffect, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { useEnforceUpgrade } from 'components/billing/useEnforceUpgrade';
import { isAvailableFeatureSelector } from 'features/billing';
import { formatDuration } from 'services/event_types';
import { DEFAULT_DURATIONS, MAX_OPTIONS_COUNT } from './constants';
import { CustomDurationField } from './custom_duration_field';
const containerCss = "cg994vu";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const dropdownMenuWideCss = "d76dpc7";
const dropdownMenuCss = "d139t4fj";
const DropdownMenu = forwardRef((props, ref) => {
  const {
    wide,
    ...restProps
  } = props;
  return <Menu ref={ref} {...restProps} className={classNames(dropdownMenuCss, {
    [dropdownMenuWideCss]: wide
  }, props.className)} />;
});
const alignRightCss = "a1daifp";
const AlignRight = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(alignRightCss, props.className)} />;
});
const upgradeButtonCss = "u172b0f4";
const UpgradeButton = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(upgradeButtonCss, props.className)} />);
const DropdownContent = ({
  defaultDuration,
  durationOptions,
  isCustomDurationOpen,
  updateDurationOptions
}) => {
  const {
    setFieldValue
  } = useFormContext();
  const showCustomDuration = () => setFieldValue('isCustomDurationOpen', true);
  const options = DEFAULT_DURATIONS.filter(d => !durationOptions.includes(d) && d !== defaultDuration).map(d => ({
    value: d,
    name: formatDuration(d, undefined, true)
  }));
  if (isCustomDurationOpen) {
    return <CustomDurationField defaultDuration={defaultDuration} durationOptions={durationOptions} updateDurationOptions={updateDurationOptions} />;
  }
  return <>
      {options.map(({
      value,
      name
    }) => <Item key={value} onClick={() => updateDurationOptions(value)}>
          {name}
        </Item>)}
      <BareItem onClick={showCustomDuration}>
        {I18n.t('publisher.event_types.availability.duration_fieldset.custom')}
        <AlignRight>
          <ArrowChevronRightMiniIcon />
        </AlignRight>
      </BareItem>
    </>;
};
export const AddDurationOption = ({
  defaultDuration,
  updateDurationOptions
}) => {
  const [toggleDropdown, Dropdown, isExpanded] = useDropdown();
  const {
    values: {
      durationOptions,
      isCustomDurationOpen,
      eventDuration,
      customEventDuration
    },
    errors: {
      customEventDuration: customEventDurationError
    },
    setFieldValue,
    setFieldTouched
  } = useFormContext();
  const isMultipleDurationsAvailable = useSelector(state => isAvailableFeatureSelector(state, {
    featureKey: 'multiple_durations'
  }));
  const upgradeSubscription = useEnforceUpgrade('multiple_durations', {
    paywall: 'general_account_settings_branding'
  });
  const disabled = !isMultipleDurationsAvailable || !!customEventDurationError || eventDuration === 'custom' && Number.isNaN(parseInt(customEventDuration, 10));
  useEffect(() => {
    if (!isExpanded) {
      setFieldValue('isCustomDurationOpen', false);
      setFieldValue('tempCustomEventDuration', '');
      setFieldValue('tempCustomEventDurationType', 'minutes');
      setFieldTouched('tempCustomEventDuration', true, true);
    }
  }, [isExpanded, setFieldValue, setFieldTouched]);
  if (durationOptions.length >= MAX_OPTIONS_COUNT) {
    return null;
  }
  return <Container>
      <MinimalButton spacingTop={12} spacingBottom={8} fontSize="small" onClick={toggleDropdown} disabled={disabled}>
        <AddMiniIcon />
        {I18n.t('publisher.event_types.availability.duration_fieldset.add_duration_option')}
        {isExpanded ? <ArrowChevronUpMiniIcon /> : <ArrowChevronDownMiniIcon />}
      </MinimalButton>
      {!isMultipleDurationsAvailable && <UpgradeButton onClick={upgradeSubscription}>
          <DollarSign color={colorIconActiveContrast} inline size="standard" />
          {' '}
          <BodyText color={colorTextActionContrast} size="medium" weight="semibold">
            {I18n.t('publisher.billing.action.upgrade')}
          </BodyText>
        </UpgradeButton>}
      <Dropdown>
        <DropdownMenu data-testid="add-duration-dropdown" wide={isCustomDurationOpen}>
          <DropdownContent defaultDuration={defaultDuration} durationOptions={durationOptions} isCustomDurationOpen={isCustomDurationOpen} updateDurationOptions={updateDurationOptions} />
        </DropdownMenu>
      </Dropdown>
    </Container>;
};

require("./add_duration_option.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./add_duration_option.js");